import { hasResourceAccess } from '@Data/utils/policyManifest';
import {
  useShowResourceRecord,
  type ShowResourceRecordFieldComponentProps,
  type ShowResourceRecordFieldValueProps,
  type ShowResourceRecordResource,
  type SdkRecordView
} from '@Pages/pageRuntime/hooks/useShowResourceRecord';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { isForbidden, isNotFound } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkResourceField,
  SdkResource,
  SdkResourceRecord,
  SdkResourceRelationPath
} from '@Pages/pageRuntime/sdk/types';

import { PolicyScope, type RecordScope } from '@/types/enums';

import { emptyStateClass } from './resourceRecordActions/utils';

export type ShowResourceRecordRenderState<
  TRecord extends Record<string, unknown> = Record<string, unknown>
> = {
  FieldLabel: React.ComponentType<ShowResourceRecordFieldComponentProps>;
  FieldValue: React.ComponentType<ShowResourceRecordFieldValueProps>;
  record: TRecord | undefined;
  fields: SdkResourceField[];
  isLoading: boolean;
};

type ShowResourceRecordProps<
  TResource extends ShowResourceRecordResource & Pick<SdkResource, 'policy_scope'>,
  TRelations extends readonly SdkResourceRelationPath<NoInfer<TResource>>[] = readonly []
> = {
  /** Resource handle returned by `Sdk.resource('<resourceId>')`. */
  resource: TResource;

  /** Record ID to display, commonly from `Sdk.helpers.useInput('record_id')` or a selected
   * DataTable row. */
  recordId: string | null | undefined;

  /** Field handles that control which fields are rendered and their order.
   * Omit to render all non-system, non-virtual fields accessible at Show scope. */
  fields?: SdkResourceField[];

  /** Relation paths to preload and expose on the render prop's typed `record`. */
  relations?: TRelations;

  /** Soft-delete subset to read from. Defaults to active records only; pass
   * `RecordScope.ActiveOrArchived` to also display archived (soft-deleted) records. */
  scope?: RecordScope;

  /** Optional container className. */
  className?: string;

  /**
   * Custom layout render prop. When provided, the default field list is replaced by
   * the children.
   *
   * `FieldLabel` renders the field's label text. `FieldValue` renders the type-aware
   * formatted value (booleans as badges, money with currency, selects as option badges,
   * etc.). Both are context-aware — they read the value from the loaded record
   * automatically; no `value` prop needed. `FieldValue` also takes a `fallback` to
   * replace the em dash shown for an empty value.
   *
   * @example
   * <Sdk.Components.ShowResourceRecord resource={students} recordId={recordId}>
   *   {({ FieldLabel, FieldValue }) => (
   *     <dl className="grid grid-cols-2 gap-4">
   *       <div>
   *         <FieldLabel field={students.fields.name} />
   *         <FieldValue field={students.fields.name} />
   *       </div>
   *       <div>
   *         <FieldLabel field={students.fields.status} />
   *         <FieldValue field={students.fields.status} fallback="Not set" />
   *       </div>
   *     </dl>
   *   )}
   * </Sdk.Components.ShowResourceRecord>
   */
  children?: (
    state: ShowResourceRecordRenderState<SdkResourceRecord<TResource, TRelations>>
  ) => React.ReactNode;
};

function ShowResourceRecordDefaultContent({
  fields,
  handle
}: {
  fields: SdkResourceField[];
  handle: SdkRecordView;
}) {
  const { FieldValue } = handle;

  return (
    <dl className="divide-y divide-border">
      {fields.map((field) => (
        <div key={field.key} className="flex items-center justify-between gap-4 py-3">
          <dt className="shrink-0 text-sm font-medium text-muted-foreground">{field.label}</dt>
          <dd className="min-w-0 text-right text-sm">
            <FieldValue field={field} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A resource-aware record detail view that fetches a record, resolves show-policy
 * fields, and renders each field with type-appropriate formatting. The display
 * equivalent of `UpdateResourceRecordForm`. Pass `relations` to preload them and expose
 * those relation records on the custom layout's typed `record`.
 *
 * @example
 * const students = Sdk.resource('students');
 *
 * <Sdk.Components.ShowResourceRecord resource={students} recordId={recordId} />
 *
 * @example
 * const students = Sdk.resource('students');
 *
 * <Sdk.Components.ShowResourceRecord resource={students} recordId={recordId}>
 *   {({ FieldLabel, FieldValue }) => (
 *     <dl className="grid grid-cols-2 gap-4">
 *       <div>
 *         <FieldLabel field={students.fields.name} />
 *         <FieldValue field={students.fields.name} />
 *       </div>
 *       <div>
 *         <FieldLabel field={students.fields.status} />
 *         <FieldValue field={students.fields.status} />
 *       </div>
 *     </dl>
 *   )}
 * </Sdk.Components.ShowResourceRecord>
 */
export function ShowResourceRecord<
  TResource extends ShowResourceRecordResource & Pick<SdkResource, 'policy_scope'>,
  const TRelations extends readonly SdkResourceRelationPath<NoInfer<TResource>>[] = readonly []
>({
  resource,
  recordId,
  fields: overrideFields,
  relations,
  scope,
  className,
  children
}: ShowResourceRecordProps<TResource, TRelations>) {
  const handle = useShowResourceRecord({
    resource,
    recordId,
    fields: overrideFields,
    relations,
    scope
  });
  const { fields, record, isLoading, error, FieldLabel, FieldValue } = handle;

  if (!hasResourceAccess(resource, Number(PolicyScope.Show)) || isForbidden(error)) {
    return (
      <div className={emptyStateClass(className)}>
        You do not have permission to view this record.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          'rounded-md border border-border p-4 text-center text-muted-foreground',
          className
        )}
      >
        Loading record...
      </div>
    );
  }

  if (isNotFound(error)) {
    return <div className={emptyStateClass(className)}>Record not found.</div>;
  }

  if (error) {
    return <div className={emptyStateClass(className)}>Unable to load this record.</div>;
  }

  const content = children ? (
    children({ FieldLabel, FieldValue, record, fields, isLoading })
  ) : (
    <ShowResourceRecordDefaultContent fields={fields} handle={handle} />
  );

  return <div className={className}>{content}</div>;
}
